/**
 * AutoDebitAuthorizationCard — unified buyer + seller settings widget.
 * Phase 1 + 2 of the ACH auto-debit feature.
 *
 * Plaid is MOCKED in preview — clicking "Link bank" jumps straight to
 * the (fake) institution + account-mask result. NACHA terms are
 * placeholder copy with a typed-name signature capture. All consent
 * metadata (consent_at / ip / user-agent / terms_version) is recorded
 * server-side for the audit chain.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Landmark, ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";


const DEFAULT_TRIGGERS = {
  wholesale_invoice:  false,
  lending_repayment:  false,
};

export default function AutoDebitAuthorizationCard({ role }) {
  const [state, setState]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [bank, setBank]       = useState(null);
  const [open, setOpen]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [triggers, setTriggers]    = useState({ ...DEFAULT_TRIGGERS });
  const [maxCharge, setMaxCharge]  = useState("5000");
  const [dailyCap, setDailyCap]    = useState("10000");
  const [monthlyCap, setMonthlyCap]= useState("50000");
  const [typedName, setTypedName]  = useState("");
  const [accepted, setAccepted]    = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ach-auth/me");
      setState(r.data);
    } catch { /* unauth or no auth yet */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const linkBank = async () => {
    setBusy(true);
    try {
      // 1. Get a Plaid Link token (mocked — real Plaid Link would open the widget)
      await api.post("/ach-auth/plaid-link");
      // 2. Simulate the user completing Link → exchange returns the bank
      const fakePublicToken = "public-mock-" + Date.now();
      const r = await api.post("/ach-auth/plaid-exchange", { public_token: fakePublicToken });
      setBank(r.data);
      setOpen(true);
      toast.success(`Bank linked: ${r.data.institution} ${r.data.account_mask}`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not link bank.");
    } finally { setBusy(false); }
  };

  const authorize = async () => {
    const triggerList = Object.entries(triggers).filter(([_, v]) => v).map(([k]) => k);
    if (!triggerList.length) { toast.error("Pick at least one trigger."); return; }
    if (!accepted) { toast.error("You must accept the authorization terms."); return; }
    if (typedName.trim().length < 3) { toast.error("Type your full name to sign."); return; }
    setBusy(true);
    try {
      await api.post("/ach-auth/authorize", {
        public_token:           "public-mock-passthrough",
        triggers:               triggerList,
        max_per_charge_cents:   Math.round(parseFloat(maxCharge) * 100),
        daily_cap_cents:        Math.round(parseFloat(dailyCap) * 100),
        monthly_cap_cents:      Math.round(parseFloat(monthlyCap) * 100),
        typed_name:             typedName,
        terms_accepted:         accepted,
      });
      toast.success("Auto-debit armed");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Authorize failed");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!confirm("Revoke ACH auto-debit authorization? Future scheduled bills will not auto-pull.")) return;
    setBusy(true);
    try {
      await api.post("/ach-auth/revoke", { reason: "user_revoked_from_settings" });
      toast.success("Authorization revoked.");
      await load();
    } catch (e) { toast.error("Revoke failed"); } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-background p-5 flex items-center gap-2 text-foreground/40 text-sm">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading auto-debit…
      </div>
    );
  }
  const armed = state?.authorization?.status === "active";

  return (
    <>
      <div className="rounded-2xl border bg-background p-5" data-testid="ach-auto-debit-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center shrink-0">
            <Landmark className="w-4 h-4"/>
          </div>
          <div className="flex-1">
            <div className="display text-lg tracking-tight">
              Auto-debit from bank when wallet is short
              {armed && <span className="ml-2 text-[11px] mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">armed</span>}
            </div>
            <p className="text-[12.5px] text-foreground/55 mt-0.5 max-w-2xl">
              When your verqo wallet doesn&apos;t have enough for a scheduled bill, verqo can pull from
              your authorized bank account via ACH — but ONLY for the bill types you authorize, and
              ONLY up to the limits you set. You can revoke anytime.
            </p>
          </div>
        </div>

        {armed ? (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg border p-3 text-[12.5px]">
              <div className="text-foreground/55 mono uppercase tracking-widest text-[10px] mb-1">Bank</div>
              <div className="font-medium">{state.authorization.bank?.institution} {state.authorization.bank?.account_mask}</div>
            </div>
            <div className="rounded-lg border p-3 text-[12.5px]">
              <div className="text-foreground/55 mono uppercase tracking-widest text-[10px] mb-1">Triggers</div>
              <div className="font-medium">{(state.authorization.triggers || []).join(" · ")}</div>
            </div>
            <div className="rounded-lg border p-3 text-[12.5px]">
              <div className="text-foreground/55 mono uppercase tracking-widest text-[10px] mb-1">Limits</div>
              <div className="font-medium">
                ${(state.authorization.max_per_charge_cents / 100).toLocaleString()} / charge ·
                ${(state.authorization.daily_cap_cents / 100).toLocaleString()} / day ·
                ${(state.authorization.monthly_cap_cents / 100).toLocaleString()} / mo
              </div>
            </div>
            <div className="rounded-lg border p-3 text-[12.5px]">
              <div className="text-foreground/55 mono uppercase tracking-widest text-[10px] mb-1">Signed</div>
              <div className="font-medium">{state.authorization.typed_name_signature} · {new Date(state.authorization.consent_at).toLocaleDateString()}</div>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button variant="outline" onClick={revoke} disabled={busy} data-testid="ach-revoke-btn">
                Revoke authorization
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <Button onClick={linkBank} disabled={busy} className="rounded-full" data-testid="ach-link-bank-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
              <Landmark className="w-3.5 h-3.5 mr-1.5"/> Link bank via Plaid
            </Button>
            <div className="text-[11px] text-foreground/45 mt-2 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3"/> Mocked in preview · routes through Plaid + ACH originator in production
            </div>
          </div>
        )}
      </div>

      {/* NACHA authorization modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ach-authorize-modal">
          <DialogHeader>
            <DialogTitle>Authorize ACH auto-debit</DialogTitle>
            <DialogDescription>
              Bank linked: <strong>{bank?.institution} {bank?.account_mask}</strong>. Configure your authorization below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Authorize for</Label>
              <div className="space-y-1.5 mt-1.5">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <Checkbox checked={triggers.wholesale_invoice} onCheckedChange={(v) => setTriggers(t => ({...t, wholesale_invoice: !!v}))} data-testid="ach-trigger-wholesale"/>
                  <span>Wholesale invoice payments</span>
                </label>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <Checkbox checked={triggers.lending_repayment} onCheckedChange={(v) => setTriggers(t => ({...t, lending_repayment: !!v}))} data-testid="ach-trigger-lending"/>
                  <span>Lending / financing repayments</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Max / charge</Label>
                <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
                  <Input type="number" value={maxCharge} onChange={e => setMaxCharge(e.target.value)} className="pl-7" data-testid="ach-max-charge"/></div>
              </div>
              <div>
                <Label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Daily cap</Label>
                <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
                  <Input type="number" value={dailyCap} onChange={e => setDailyCap(e.target.value)} className="pl-7" data-testid="ach-daily-cap"/></div>
              </div>
              <div>
                <Label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Monthly cap</Label>
                <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
                  <Input type="number" value={monthlyCap} onChange={e => setMonthlyCap(e.target.value)} className="pl-7" data-testid="ach-monthly-cap"/></div>
              </div>
            </div>

            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>
                <strong>Placeholder terms · draft v1.</strong> The full NACHA-compliant authorization
                language is pending legal review. By signing below you agree to verbal-equivalent
                e-sign capture of your consent (timestamp, IP, browser) for audit purposes.
              </div>
            </div>

            <div>
              <Label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Type your full name to sign</Label>
              <Input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="Jane Doe" className="mt-1" data-testid="ach-typed-name"/>
            </div>

            <label className="flex items-start gap-2 text-[13px] cursor-pointer">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="ach-accept-terms" className="mt-0.5"/>
              <span>I authorize verqo to ACH-debit my linked bank account per the rules above. I can revoke anytime from Settings.</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={authorize} disabled={busy} className="bg-emerald-700 hover:bg-emerald-700/90 text-white" data-testid="ach-authorize-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/> Authorize &amp; save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
